/** 附件／來源／產物／分析圖表的安全整形：不顯示未授權檔案 ID，不把錯誤來源當證據。 */

const FILE_READY = new Set(['ready', 'extracted', 'ok', 'complete']);
const FILE_ERROR = new Set(['error', 'failed', 'unsupported', 'denied', 'forbidden']);
const RESEARCH_STATUSES = new Set(['complete', 'checkpoint', 'incomplete', 'resumed', 'partial']);

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function looksLikeSecret(text) {
  return /api[_-]?key|token=|bearer\s+[a-z0-9]|sk-[a-z0-9]/i.test(String(text || ''));
}

const ASSISTANT_DOWNLOAD_PATH = /^\/api\/assistant\/files\/([A-Za-z0-9_-]{1,80})\/download$/;

export function assistantFileDownloadPath(fileId) {
  if (typeof fileId !== 'string') return '';
  const id = fileId.trim();
  if (!id || id.length > 80 || !/^[A-Za-z0-9_-]+$/.test(id)) return '';
  return `/api/assistant/files/${id}/download`;
}

export function sanitizeDownloadUrl(value) {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw || raw.length > 2000) return '';
  const pathOnly = raw.split('?')[0].split('#')[0];
  if (pathOnly.startsWith('/')) {
    if (pathOnly.includes('..') || pathOnly.includes('\\') || pathOnly.includes('://')) return '';
    return ASSISTANT_DOWNLOAD_PATH.test(pathOnly) ? pathOnly : '';
  }
  try {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    const parsed = new URL(raw);
    if (!origin || parsed.origin !== origin) return '';
    if (parsed.username || parsed.password) return '';
    return ASSISTANT_DOWNLOAD_PATH.test(parsed.pathname) ? parsed.pathname : '';
  } catch {
    return '';
  }
}

function downloadUrlFromAsset(obj) {
  const fromField = sanitizeDownloadUrl(obj.download_url || obj.downloadUrl);
  if (fromField) return fromField;
  const id = typeof obj.id === 'string' ? obj.id.trim() : '';
  return assistantFileDownloadPath(id);
}

export function ownedIdSet(attachments) {
  const set = new Set();
  for (const item of Array.isArray(attachments) ? attachments : []) {
    if (item && typeof item.id === 'string' && item.id.trim()) set.add(item.id.trim());
  }
  return set;
}

function sourceLocator(source) {
  const obj = asObject(source);
  if (!obj) return '';
  const parts = [];
  if (typeof obj.filename === 'string' && obj.filename.trim()) parts.push(obj.filename.trim().slice(0, 180));
  else if (typeof obj.name === 'string' && obj.name.trim()) parts.push(obj.name.trim().slice(0, 180));
  if (obj.page != null && Number.isFinite(Number(obj.page))) parts.push(`第 ${Number(obj.page)} 頁`);
  else if (typeof obj.pages === 'string' && obj.pages.trim()) parts.push(`頁 ${obj.pages.trim().slice(0, 40)}`);
  if (typeof obj.sheet === 'string' && obj.sheet.trim()) parts.push(`工作表 ${obj.sheet.trim().slice(0, 80)}`);
  if (typeof obj.cell === 'string' && obj.cell.trim()) parts.push(obj.cell.trim().slice(0, 40));
  else if (typeof obj.cells === 'string' && obj.cells.trim()) parts.push(obj.cells.trim().slice(0, 40));
  if (typeof obj.label === 'string' && obj.label.trim() && !parts.length) parts.push(obj.label.trim().slice(0, 180));
  if (typeof obj.locator === 'string' && obj.locator.trim() && !parts.length) parts.push(obj.locator.trim().slice(0, 180));
  return parts.join(' · ');
}

function flattenCitationLocators(citation) {
  const obj = asObject(citation);
  if (!obj) return [];
  const locators = Array.isArray(obj.locators) ? obj.locators : [];
  if (locators.length) {
    return locators.map((locator) => {
      const row = asObject(locator) || {};
      return {
        file_id: typeof obj.id === 'string' ? obj.id : (typeof obj.file_id === 'string' ? obj.file_id : ''),
        filename: row.filename || row.name || obj.name || obj.filename || '',
        name: obj.name || '',
        page: row.page,
        sheet: row.sheet,
        cell: row.cell || row.cells,
        cells: row.cells,
        status: obj.status,
      };
    });
  }
  if (obj.type === 'file' || obj.filename || obj.name) {
    return [{
      file_id: typeof obj.id === 'string' ? obj.id : (typeof obj.file_id === 'string' ? obj.file_id : ''),
      filename: obj.filename || obj.name || '',
      name: obj.name || '',
      page: obj.page,
      sheet: obj.sheet,
      cell: obj.cell || obj.cells,
      cells: obj.cells,
      status: obj.status,
    }];
  }
  return [];
}

export function fileSourcesFromResult(result, ownedIds) {
  const obj = asObject(result);
  if (!obj) return [];
  if (Array.isArray(obj.file_sources) && obj.file_sources.length) {
    return sanitizeFileSources(obj.file_sources, ownedIds);
  }
  if (Array.isArray(obj.attachment_sources) && obj.attachment_sources.length) {
    return sanitizeFileSources(obj.attachment_sources, ownedIds);
  }
  if (Array.isArray(obj.citations) && obj.citations.length) {
    const fileCitations = obj.citations
      .filter((item) => asObject(item)?.type === 'file' || asObject(item)?.kind === 'file')
      .flatMap(flattenCitationLocators);
    return sanitizeFileSources(fileCitations, ownedIds);
  }
  return [];
}

export function sanitizeFileSources(input, ownedIds) {
  if (!Array.isArray(input)) return [];
  const owned = ownedIds instanceof Set ? ownedIds : null;
  const out = [];
  for (const item of input) {
    const obj = asObject(item);
    if (!obj) continue;
    const fileId = typeof obj.file_id === 'string' ? obj.file_id.trim() : (typeof obj.id === 'string' ? obj.id.trim() : '');
    if (fileId && owned && !owned.has(fileId)) continue;
    const status = typeof obj.status === 'string' ? obj.status.toLowerCase() : '';
    const rawError = typeof obj.error === 'string' ? obj.error : (typeof obj.message === 'string' ? obj.message : '');
    const error = FILE_ERROR.has(status) || obj.ok === false
      ? (rawError && !looksLikeSecret(rawError) ? rawError.slice(0, 200) : '這個檔案沒有可用的提取來源。')
      : '';
    const label = sourceLocator(obj);
    if (!label && !error) continue;
    out.push({
      label: label || '附件',
      error,
      filename: typeof obj.filename === 'string' ? obj.filename : '',
    });
    if (out.length >= 12) break;
  }
  return out;
}

export function sanitizeAttachment(input, ownedIds) {
  const obj = asObject(input);
  if (!obj) return null;
  const id = typeof obj.id === 'string' ? obj.id.trim() : '';
  const name = typeof obj.name === 'string' ? obj.name.trim().slice(0, 240) : '';
  if (!id || !name) return null;
  if (ownedIds instanceof Set && ownedIds.size && !ownedIds.has(id)) return null;
  const statusRaw = typeof obj.status === 'string' ? obj.status.toLowerCase() : '';
  let status = 'uploaded';
  if (FILE_READY.has(statusRaw)) status = 'ready';
  else if (FILE_ERROR.has(statusRaw)) status = 'error';
  else if (statusRaw === 'processing' || statusRaw === 'pending') status = 'processing';
  else if (statusRaw) status = 'uploaded';
  const downloadUrl = downloadUrlFromAsset(obj);
  const sources = sanitizeFileSources(obj.sources, ownedIds instanceof Set ? new Set([id, ...ownedIds]) : new Set([id]));
  const size = Number.isFinite(Number(obj.size)) ? Number(obj.size) : undefined;
  const mime = typeof obj.mime === 'string' ? obj.mime.slice(0, 120) : '';
  const error = status === 'error'
    ? (typeof obj.error === 'string' && !looksLikeSecret(obj.error)
      ? obj.error.slice(0, 200)
      : '檔案無法使用：沒有成功的來源提取。')
    : '';
  return {
    id,
    name,
    mime,
    size,
    status,
    sources,
    downloadUrl,
    error,
  };
}

export function normalizeUploadedFiles(payload, ownedIds) {
  if (Array.isArray(payload)) return payload.map((item) => sanitizeAttachment(item, ownedIds)).filter(Boolean);
  const obj = asObject(payload);
  if (!obj) return [];
  if (Array.isArray(obj.files)) return obj.files.map((item) => sanitizeAttachment(item, ownedIds)).filter(Boolean);
  const single = sanitizeAttachment(obj, ownedIds);
  return single ? [single] : [];
}

export function readyAttachmentIds(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter((item) => item && item.status === 'ready' && typeof item.id === 'string')
    .map((item) => item.id);
}

export function visibleAttachments(attachments, ownedIds) {
  const owned = ownedIds instanceof Set ? ownedIds : ownedIdSet(attachments);
  return (Array.isArray(attachments) ? attachments : [])
    .map((item) => sanitizeAttachment(item, owned))
    .filter(Boolean);
}

export function sanitizeArtifact(input, ownedIds) {
  const obj = asObject(input);
  if (!obj) return null;
  const id = typeof obj.id === 'string' ? obj.id.trim() : '';
  const name = typeof obj.name === 'string' && obj.name.trim()
    ? obj.name.trim().slice(0, 240)
    : (typeof obj.filename === 'string' ? obj.filename.trim().slice(0, 240) : '');
  const downloadUrl = downloadUrlFromAsset(obj);
  if (!downloadUrl) return null;
  const mime = typeof obj.mime === 'string' ? obj.mime.slice(0, 120) : '';
  const sources = sanitizeFileSources(obj.sources, ownedIds);
  return { id, name: name || '產物', mime, downloadUrl, sources };
}

export function sanitizeImageResult(input) {
  const obj = asObject(input);
  if (!obj) return null;
  if (obj.not_configured === true) return null;
  const url = sanitizeDownloadUrl(obj.url || obj.download_url || obj.image_url || obj.downloadUrl)
    || downloadUrlFromAsset(obj);
  if (!url) return null;
  const alt = typeof obj.alt === 'string' && obj.alt.trim() ? obj.alt.trim().slice(0, 200) : '產生的圖像';
  return { url, alt, name: typeof obj.name === 'string' ? obj.name : '', downloadUrl: url };
}

function finiteNumbers(list) {
  return (Array.isArray(list) ? list : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
}

function numericFromCell(cell) {
  if (typeof cell === 'number' && Number.isFinite(cell)) return cell;
  if (typeof cell === 'string' && cell.trim() && Number.isFinite(Number(cell))) return Number(cell);
  if (asObject(cell) && Number.isFinite(Number(cell.value))) return Number(cell.value);
  return null;
}

export function extractChartSeries(analysis) {
  const obj = asObject(analysis);
  if (!obj) return null;
  const chart = asObject(obj.chart);
  if (chart) {
    const labels = Array.isArray(chart.labels) ? chart.labels.map((label) => String(label).slice(0, 40)) : [];
    if (Array.isArray(chart.datasets) && chart.datasets.length) {
      const datasets = chart.datasets.slice(0, 4).map((set, index) => {
        const row = asObject(set) || {};
        return {
          label: typeof row.label === 'string' && row.label.trim() ? row.label.trim().slice(0, 40) : `數列 ${index + 1}`,
          values: finiteNumbers(row.values || row.data),
        };
      }).filter((set) => set.values.length);
      if (datasets.length) {
        const maxLabel = Math.max(...datasets.map((set) => set.values.length), labels.length);
        const paddedLabels = labels.slice();
        while (paddedLabels.length < maxLabel) paddedLabels.push(String(paddedLabels.length + 1));
        return {
          type: chart.type === 'line' ? 'line' : 'bar',
          title: typeof chart.title === 'string' ? chart.title.slice(0, 120) : '',
          labels: paddedLabels.slice(0, datasets[0].values.length),
          datasets,
        };
      }
    }
    const values = finiteNumbers(chart.values || chart.data || chart.points);
    if (values.length) {
      const labelsOut = labels.length ? labels.slice(0, values.length) : values.map((_, i) => String(i + 1));
      const label = typeof chart.label === 'string' && chart.label.trim() ? chart.label.trim().slice(0, 40) : '';
      return {
        type: chart.type === 'line' ? 'line' : 'bar',
        title: typeof chart.title === 'string' ? chart.title.slice(0, 120) : '',
        labels: labelsOut,
        datasets: [{ label, values }],
      };
    }
  }
  const columns = Array.isArray(obj.columns) ? obj.columns.map((col) => (typeof col === 'string' ? col : col?.name || '')).filter(Boolean) : [];
  const rows = Array.isArray(obj.rows) ? obj.rows : [];
  if (!rows.length) return null;
  const numericIndex = [];
  const sample = Array.isArray(rows[0]) ? rows[0] : (asObject(rows[0]) ? columns.map((c) => rows[0][c]) : []);
  (Array.isArray(sample) ? sample : []).forEach((cell, index) => {
    if (numericFromCell(cell) != null) numericIndex.push(index);
  });
  const valueIndex = numericIndex.length ? numericIndex[numericIndex.length - 1] : -1;
  if (valueIndex < 0) return null;
  const labelIndex = valueIndex === 0 ? -1 : 0;
  const values = [];
  const labels = [];
  for (const row of rows.slice(0, 24)) {
    const cells = Array.isArray(row) ? row : (asObject(row) ? columns.map((c) => row[c]) : []);
    const num = numericFromCell(cells[valueIndex]);
    if (num == null) continue;
    values.push(num);
    labels.push(labelIndex >= 0 ? String(cells[labelIndex] ?? values.length).slice(0, 40) : String(values.length));
  }
  if (!values.length) return null;
  const valueLabel = columns[valueIndex] || '';
  return { type: 'bar', title: '', labels, datasets: [{ label: valueLabel, values }] };
}

function normalizeStatistics(value) {
  const obj = asObject(value);
  if (!obj) return null;
  const out = {};
  for (const [key, raw] of Object.entries(obj).slice(0, 24)) {
    const nested = asObject(raw);
    if (nested) {
      const stats = {};
      for (const [statKey, statVal] of Object.entries(nested).slice(0, 12)) {
        if (statVal == null || typeof statVal === 'string' || typeof statVal === 'number' || typeof statVal === 'boolean') {
          stats[statKey] = statVal;
        }
      }
      out[key] = stats;
    } else if (raw == null || typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      out[key] = raw;
    }
  }
  return Object.keys(out).length ? out : null;
}

export function sanitizeAnalysis(input, ownedIds) {
  const obj = asObject(input);
  if (!obj) return null;
  const summary = typeof obj.summary === 'string' ? obj.summary.slice(0, 4000) : '';
  const columns = Array.isArray(obj.columns)
    ? obj.columns.map((col) => (typeof col === 'string' ? col : String(col?.name || ''))).filter(Boolean).slice(0, 24)
    : [];
  const rows = Array.isArray(obj.rows) ? obj.rows.slice(0, 50) : [];
  const rowCount = Number.isFinite(Number(obj.row_count))
    ? Number(obj.row_count)
    : (Number.isFinite(Number(obj.rows)) && !Array.isArray(obj.rows) ? Number(obj.rows) : rows.length);
  const statistics = normalizeStatistics(obj.statistics);
  const sources = sanitizeFileSources(obj.sources, ownedIds);
  const chart = extractChartSeries(obj);
  if (!summary && !columns.length && !rows.length && !chart && !statistics) return null;
  return { summary, columns, rows, rowCount, statistics, sources, chart };
}

export function researchContinuation(research, extras = {}) {
  const obj = asObject(research) ? research : (typeof research === 'string' ? { status: research } : null);
  const status = typeof extras.status === 'string'
    ? extras.status
    : (typeof obj?.status === 'string' ? obj.status : (typeof obj?.research_status === 'string' ? obj.research_status : ''));
  const resumable = extras.resumable === true
    || obj?.resumable === true
    || extras.checkpoint === true
    || obj?.checkpoint === true;
  const canResume = resumable || status === 'checkpoint' || status === 'incomplete';
  return {
    status: RESEARCH_STATUSES.has(status) ? status : '',
    resumable: canResume,
    persistent: extras.persistent === true || obj?.persistent === true,
  };
}

export function researchMetaCopy(research, extras = {}) {
  const meta = researchContinuation(research, extras);
  if (!meta.status && !meta.resumable) return '';
  if (meta.resumable && (meta.status === 'checkpoint' || meta.status === 'incomplete' || !meta.status)) {
    return meta.persistent
      ? '深入查核已停在檢查點。同一對話再送出會繼續，伺服器已保留此檢查點。'
      : '深入查核已停在檢查點。同一對話再送出會繼續；這次沒有收到伺服器持久化旗標。';
  }
  if (meta.status === 'resumed') return '已接續同一對話的深入查核。';
  if (meta.status === 'partial') return '深入查核只完成一部分，來源仍以實際引用為準。';
  if (meta.status === 'complete') return '深入查核這輪已完成。';
  return '';
}

export function shouldResumeResearch(messages) {
  const msgs = Array.isArray(messages) ? messages : [];
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    const msg = msgs[i];
    if (msg?.researchResumable === true) return true;
    const status = msg?.researchStatus;
    if (status === 'checkpoint' || status === 'incomplete') return true;
    if (status === 'complete' || status === 'resumed' || status === 'partial') return false;
  }
  return false;
}

export function enrichChatItemWithTools(item, result, ownedIds) {
  if (!item || item.role !== 'bot') return item;
  const fileSources = fileSourcesFromResult(result, ownedIds);
  const research = asObject(result?.research) || asObject(result?.researchState) || null;
  const researchStatus = typeof result?.research_status === 'string'
    ? result.research_status
    : (typeof research?.status === 'string' ? research.status : '');
  const persistent = result?.history_status?.persistent === true
    || research?.persistent === true;
  const continuation = researchContinuation(research || researchStatus, {
    status: researchStatus,
    resumable: result?.resumable === true || research?.resumable === true,
    persistent,
    checkpoint: researchStatus === 'checkpoint' || research?.status === 'checkpoint',
  });
  const extra = { ...item };
  if (fileSources.length) extra.fileSources = fileSources;
  if (RESEARCH_STATUSES.has(continuation.status)) extra.researchStatus = continuation.status;
  extra.researchResumable = continuation.resumable;
  const researchText = researchMetaCopy(research || researchStatus, continuation);
  if (researchText) extra.researchNotice = researchText;
  const analysis = sanitizeAnalysis(result?.analysis, ownedIds);
  if (analysis) extra.analysis = analysis;
  const artifact = sanitizeArtifact(result?.artifact, ownedIds);
  if (artifact) extra.artifact = artifact;
  return extra;
}

export function toolItemsFromIndependentResult(kind, payload, ownedIds) {
  if (kind === 'analysis') {
    const analysis = sanitizeAnalysis(payload, ownedIds);
    if (!analysis) return [];
    return [{ role: 'bot', kind: 'analysis', text: analysis.summary || '分析結果如下。', analysis }];
  }
  if (kind === 'artifact') {
    const artifact = sanitizeArtifact(payload, ownedIds);
    if (!artifact) return [];
    return [{
      role: 'bot',
      kind: 'artifact',
      text: `已產生檔案「${artifact.name}」。這是依你提供的內容產出，不是已查證的研究事實。`,
      artifact,
    }];
  }
  if (kind === 'image') {
    const image = sanitizeImageResult(payload);
    if (!image) return [];
    return [{ role: 'bot', kind: 'image', text: '已依你確認的提示產生圖像。這不是查證過的事實來源。', image }];
  }
  return [];
}

export function collectFileIds(result) {
  const ids = [];
  const take = (value) => {
    if (typeof value === 'string' && value.trim()) ids.push(value.trim());
  };
  const obj = asObject(result);
  if (!obj) return ids;
  take(obj.file_id);
  if (Array.isArray(obj.attachment_ids)) obj.attachment_ids.forEach(take);
  if (Array.isArray(obj.attachments)) {
    obj.attachments.forEach((item) => take(item?.id || item?.file_id));
  }
  if (Array.isArray(obj.file_sources)) {
    obj.file_sources.forEach((item) => take(item?.file_id || item?.id));
  }
  if (Array.isArray(obj.attachment_sources)) {
    obj.attachment_sources.forEach((item) => take(item?.file_id || item?.id));
  }
  if (Array.isArray(obj.citations)) {
    obj.citations.forEach((item) => take(item?.id || item?.file_id));
  }
  const analysis = asObject(obj.analysis);
  if (analysis) {
    take(analysis.file_id);
    if (Array.isArray(analysis.sources)) analysis.sources.forEach((item) => take(item?.file_id || item?.id));
  }
  const artifact = asObject(obj.artifact);
  if (artifact && Array.isArray(artifact.sources)) {
    artifact.sources.forEach((item) => take(item?.file_id || item?.id));
  }
  return ids;
}

export function stripUnauthorizedIdsFromText(text, candidateIds, ownedIds) {
  if (typeof text !== 'string') return '';
  let next = text;
  const owned = ownedIds instanceof Set ? ownedIds : new Set();
  for (const id of Array.isArray(candidateIds) ? candidateIds : []) {
    if (typeof id !== 'string' || !id || owned.has(id)) continue;
    next = next.split(id).join('');
  }
  return next;
}
